import { useState } from 'react'
import { Sparkles, Send, AlertTriangle } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface QueryResult {
  question: string
  sql: string
  backend?: string
  columns?: string[]
  rows?: Record<string, any>[]
  row_count?: number
  error?: string
}

const EXAMPLES = [
  "Show me all KEV CVEs added in the last 7 days",
  "Top 20 critical CVEs from Microsoft this month",
  "Articles mentioning Sandworm in the last 30 days",
  "How many incidents were opened in the last 7 days, grouped by severity?",
  "Show me threat-intel IOCs from ThreatFox added in the last 24 hours",
  "Which CVEs have the highest EPSS scores right now?",
  "List all leavers in the next 14 days",
  "Show me the most recent 10 ransomware-flagged KEV CVEs",
]

export default function AskPage() {
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<QueryResult | null>(null)

  const ask = async (question?: string) => {
    const text = (question ?? q).trim()
    if (text.length < 5) return
    setQ(text)
    setLoading(true); setResult(null)
    try {
      const r = await api.post('/api/cyber/nl-query', { question: text })
      setResult(r.data)
    } catch (e: any) {
      setResult({ question: text, sql: '', error: e?.response?.data?.detail || e.message })
    } finally { setLoading(false) }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Sparkles size={24} className="text-purple-300" /> Ask
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Plain English → safe SQL → live data. Whitelisted to SELECT-only over the cyber + ops tables.
        </p>
      </div>

      <div className="card p-4">
        <div className="flex items-center gap-2">
          <input value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && ask()}
            placeholder="e.g. Show me all KEV CVEs from Cisco in the last 30 days"
            className="input flex-1 py-2 text-sm" />
          <button type="button" onClick={() => ask()} disabled={loading} className="btn-primary text-sm">
            <Send size={12} /> {loading ? 'Thinking…' : 'Ask'}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {EXAMPLES.map(ex => (
            <button key={ex} type="button" onClick={() => ask(ex)}
              className="text-[10px] px-2 py-1 rounded border border-border hover:bg-surface-hover text-text-secondary">
              {ex}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="card p-6 text-center text-text-muted text-sm">Generating SQL + querying…</div>}

      {result && (
        <>
          {result.error && (
            <div className="card p-3 border border-rose/40 bg-rose/5 text-[12px] text-rose-light">
              <AlertTriangle size={12} className="inline mr-1" /> {result.error}
            </div>
          )}
          {result.sql && (
            <details className="card p-3" open={false}>
              <summary className="text-[10px] uppercase tracking-wider text-text-muted cursor-pointer">
                Generated SQL · {result.backend || 'unknown'} backend
              </summary>
              <pre className="text-[11px] font-mono text-text-secondary mt-2 overflow-x-auto whitespace-pre-wrap">{result.sql}</pre>
            </details>
          )}

          {result.rows && result.rows.length > 0 && (
            <div className="card p-3 overflow-x-auto">
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">
                {result.row_count || result.rows.length} row{result.rows.length === 1 ? '' : 's'}
              </div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                    {(result.columns || Object.keys(result.rows[0])).map(c => (
                      <th key={c} className="text-left py-2 px-2">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.slice(0, 100).map((row, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-surface-hover">
                      {(result.columns || Object.keys(row)).map(c => (
                        <td key={c} className="py-1.5 px-2 align-top">
                          <span className="text-text-primary font-mono">
                            {typeof row[c] === 'object' ? JSON.stringify(row[c]) : String(row[c] ?? '')}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result.rows && result.rows.length === 0 && !result.error && (
            <div className="card p-6 text-center text-text-muted text-sm">No rows returned.</div>
          )}
        </>
      )}
    </div>
  )
}
